import { useMemo, useState } from 'react';
import { Circle, CheckCircle2, Loader2, ListTodo, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCompletarTarea, useTareasLista } from '@/hooks/useTareas';
import type { EntidadTipoTarea, FiltrosTareas } from '@/types/tareas';
import { TareaFormDialog } from '@/pages/tareas/TareaFormDialog';
import { TareaDetailModal } from '@/pages/tareas/TareaDetailModal';
import { AvatarsAsignados, PrioridadBadge, VencimientoTexto } from './TareaPiezas';

const MAX_TAREAS = 50;

type Filtro = 'abiertas' | 'completadas' | 'todas';

interface Props {
  entidadTipo: Extract<EntidadTipoTarea, 'paciente' | 'cita' | 'presupuesto'>;
  entidadId: string;
  /** Texto del chip al crear una tarea desde aquí (nombre del paciente, "Cita · 30 jul", título…). */
  etiqueta: string;
  /** Versión para espacios angostos (diálogo de cita): oculta prioridad y asignados. */
  compacta?: boolean;
  className?: string;
}

/**
 * Tareas ligadas a un paciente, cita o presupuesto, con alta rápida. Al ligar desde aquí, el
 * backend agrega solo al paciente de una cita/presupuesto, así que la ficha del paciente muestra
 * también las tareas de sus citas y presupuestos. La consulta se abre por cita, y las tareas
 * ligadas a la consulta de esa cita también aparecen en la cita.
 */
export function TareasRelacionadasPanel({ entidadTipo, entidadId, etiqueta, compacta = false, className }: Props) {
  const [filtro, setFiltro] = useState<Filtro>('abiertas');
  const [nueva, setNueva] = useState(false);
  const [abierta, setAbierta] = useState<string | null>(null);
  const completar = useCompletarTarea();

  const filtros = useMemo<FiltrosTareas>(() => ({
    entidadTipo,
    entidadId,
    limit: MAX_TAREAS,
    completadas: filtro === 'abiertas' ? 'no' : filtro === 'completadas' ? 'si' : undefined,
  }), [entidadTipo, entidadId, filtro]);

  const { data, isLoading, isError } = useTareasLista(filtros);
  const relacionesIniciales = useMemo(() => [{ entidadTipo, entidadId, label: etiqueta }], [entidadTipo, entidadId, etiqueta]);

  return (
    <section className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-text-primary mr-auto">
          <ListTodo size={15} className="text-text-secondary" /> Tareas
          {data && <span className="text-xs font-normal text-text-secondary">({data.total})</span>}
        </h3>
        <select
          value={filtro}
          onChange={e => setFiltro(e.target.value as Filtro)}
          className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
          aria-label="Filtrar tareas"
        >
          <option value="abiertas">Abiertas</option>
          <option value="completadas">Completadas</option>
          <option value="todas">Todas</option>
        </select>
        <button
          onClick={() => setNueva(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600"
        >
          <Plus size={13} /> Nueva tarea
        </button>
      </div>

      {isLoading && <div className="py-8 flex justify-center text-gray-400"><Loader2 className="animate-spin" /></div>}
      {isError && <p className="py-6 text-center text-sm text-text-secondary">No se pudieron cargar las tareas.</p>}
      {data && data.items.length === 0 && (
        <p className="py-8 text-center text-sm text-text-secondary">
          {filtro === 'abiertas' ? 'Sin tareas abiertas.' : filtro === 'completadas' ? 'Sin tareas completadas.' : 'Aún no hay tareas.'}
        </p>
      )}

      <ul className="space-y-1.5">
        {data?.items.map(t => (
          <li
            key={t.id}
            onClick={() => setAbierta(t.id)}
            className="flex items-center gap-2.5 rounded-lg border border-border bg-white px-3 py-2 hover:border-primary-300 cursor-pointer"
          >
            <button
              onClick={e => { e.stopPropagation(); completar.mutate({ id: t.id, completar: !t.completadaAt }); }}
              className="text-gray-300 hover:text-emerald-500 flex-shrink-0"
              aria-label={t.completadaAt ? 'Reabrir tarea' : 'Completar tarea'}
            >
              {t.completadaAt ? <CheckCircle2 size={19} className="text-emerald-500" /> : <Circle size={19} />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium truncate', t.completadaAt ? 'line-through text-text-secondary' : 'text-text-primary')}>{t.titulo}</p>
              <p className="text-[11px] text-text-secondary truncate">{t.tableroNombre} · {t.columnaNombre}</p>
            </div>
            <VencimientoTexto tarea={t} />
            {!compacta && <PrioridadBadge prioridad={t.prioridad} className="hidden sm:inline-flex" />}
            {!compacta && <AvatarsAsignados asignados={t.asignados} />}
          </li>
        ))}
      </ul>
      {data && data.total > data.items.length && (
        <p className="text-center text-xs text-text-secondary">Mostrando {data.items.length} de {data.total}. Usa el módulo Tareas para ver todas.</p>
      )}

      <TareaFormDialog open={nueva} onOpenChange={setNueva} relacionesIniciales={relacionesIniciales} />
      <TareaDetailModal tareaId={abierta} onClose={() => setAbierta(null)} onAbrirTarea={setAbierta} />
    </section>
  );
}
